import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import IEslTemplateItem, { IEslTemplateItemDimensions, IEslTemplateItemStyle } from '../../../models/esl/eslTemplateElement';
import { EEslTemplateElementType } from '../../../models/enums/eslTemplateType';
import ElementPicker from './ElementsPicker';
import ElementPropreties from './ElementPropreties';
import IEslTemplate from '../../../models/esl/eslTemplate';
import TemplateInfo from './TemplateInfo';
import { EEslDatetimeLanguage } from '../../../models/enums/eslDatetimeLanguage';
import EditOnly from '../../permissions/EditOnly';

export interface IOnAddElementProps {
  type: EEslTemplateElementType;
  customStyle?: IEslTemplateItemStyle;
  customDimensions?: IEslTemplateItemDimensions;
}
interface ISidebarProps {
  selectedElementId: number;
  setSelectedElementId: Dispatch<SetStateAction<number>>;
  template: IEslTemplate;
  setTemplate: (template: IEslTemplate) => void;
}

const DEFAULT_TEXT_CONTENT = 'Aa';
const BLACK = '#000000';

const Sidebar = ({ selectedElementId, setSelectedElementId, template, setTemplate }: ISidebarProps) => {
  const [selectedElement, setSelectedElement] = useState<IEslTemplateItem | null>(null);

  useEffect(() => {
    if (!selectedElementId) return setSelectedElement(null);

    const element = template.elements.find((el) => el.id === selectedElementId);
    if (element) setSelectedElement(element);
  }, [selectedElementId, template.elements]);

  const onAddElement = ({ type, customStyle, customDimensions }: IOnAddElementProps) => {
    const newElement: IEslTemplateItem = {
      id: template.elements.length > 0 ? template.elements[template.elements.length - 1].id + 1 : 1,
      type,
      textContent: DEFAULT_TEXT_CONTENT,
      imageSrc: '',
      position: {
        left: 50,
        top: 50,
      },
      dimensions: {
        width: customDimensions?.width || 100,
        height: customDimensions?.height || 100,
      },
      style: {
        backgroundColor: customStyle?.backgroundColor || '',
        color: customStyle?.color || BLACK,
        fontSize: customStyle?.fontSize || 16,
        isBold: customStyle?.isBold || false,
        isItalic: customStyle?.isItalic || false,
        isUnderline: customStyle?.isUnderline || false,
        textAlign: customStyle?.textAlign || 'left',
      },
      language: EEslDatetimeLanguage.French,
    }

    setTemplate({ ...template, elements: [...template.elements, newElement] });
    setSelectedElementId(newElement.id);
  }

  const onUpdateElement = (updatedElement: IEslTemplateItem) => {
    setTemplate({
      ...template,
      elements: template.elements.map((el) => el.id === updatedElement.id ? updatedElement : el),
    });
  };

  const onDeleteElement = (id: number) => {
    setTemplate({
      ...template,
      elements: template.elements.filter((el) => el.id !== id),
    });
    setSelectedElementId(0);
  };

  return (
    <Box sx={{ height: "100%", padding: 2, bgcolor: 'background.paper', borderLeft: '1px solid #ccc' }}>
      <TemplateInfo template={template} setTemplate={setTemplate} />
      <Box sx={{ mt: 4, mb: 2, borderBottom: '1px solid #ccc' }} />

      <EditOnly>
        <ElementPicker onAddElement={onAddElement} currentElements={template.elements} />
        <Box sx={{ mt: 4, mb: 2, borderBottom: '1px solid #ccc' }} />
      </EditOnly>

      {selectedElement && (
        <ElementPropreties
          selectedElement={selectedElement}
          onUpdateElement={onUpdateElement}
          onDeleteElement={onDeleteElement}
          templateWidth={template.width}
          templateHeight={template.height}
        />
      )}
    </Box>
  );
};

export default Sidebar;
